<template lang="pug">
    v-form.c-form(autocomplete="off")
        v-container(fluid grid-list-lg)
            v-layout(raw wrap)
                v-flex(:class="(customFields && customFields.length && !isAdmin) ? 'xs6' : 'xs12'")

                    // User information block title
                    title-component(
                        type="h4"
                        :title="$t('users.manualCreate.subtitleUserInformation')"
                    )

                v-flex(xs6 v-if="customFields && customFields.length && !isAdmin")
                    // User custom fields block title
                    title-component(
                        v-if="customFields && customFields.length"
                        type="h4"
                        :title="$t('users.manualCreate.subtitleCustomFields')"
                    )

                v-flex(xs12 sm6)

                    // Full Name field
                    v-text-field(
                        :error-messages="errors.collect('fullName')"
                        :data-vv-as="$t('fields.fullName')"
                        :label="$t('fields.fullName')"
                        name="fullName"
                        v-model="model.fullName"
                        v-validate="'required|min:2|max:72'"
                        outline
                    )


                    // Contact Email field
                    v-text-field(
                        :error-messages="errors.collect('email')"
                        :data-vv-as="$t('fields.email')"
                        :label="$t('fields.email')"
                        name="email"
                        type="email"
                        v-model.lazy="model.email"
                        v-validate="'required|email'"
                        outline
                    )


                    // Contact Phone field
                    v-text-field(
                        :error-messages="errors.collect('phone')"
                        :data-vv-as="$t('fields.phone')"
                        :label="$t('fields.phone')"
                        name="phone"
                        v-model.lazy="model.phone"
                        v-validate="'required|min:6'"
                        mask="###-###-######-####"
                        outline
                    )


                    // Status select
                    v-select(
                        :items="$t('selection.status')"
                        :label="$t('fields.status')"
                        name="status"
                        v-model="model.isActive"
                        append-icon="expand_more"
                        outline
                    )



                    // City field
                    v-text-field(
                        :error-messages="errors.collect('city')"
                        :data-vv-as="$t('fields.city')"
                        :label="$t('fields.city')"
                        name="city"
                        v-model="model.city"
                        v-validate="'required|min:2|max:100'"
                        outline
                    )

                    // State field
                    v-text-field(
                        :error-messages="errors.collect('state')"
                        :data-vv-as="$t('fields.state')"
                        :label="$t('fields.state')"
                        name="state"
                        v-model="model.state"
                        v-validate="'required|min:2|max:100'"
                        outline
                    )

                    // State field
                    v-text-field(
                        :error-messages="errors.collect('country')"
                        :data-vv-as="$t('fields.country')"
                        :label="$t('fields.country')"
                        name="country"
                        v-model="model.country"
                        v-validate="'required|min:2|max:100'"
                        outline
                    )

                    // Dept field
                    v-text-field(
                        :error-messages="errors.collect('department')"
                        :data-vv-as="$t('fields.department')"
                        :label="$t('fields.department')"
                        name="department"
                        v-model="model.department"
                        v-if="maxUsers >= 15"
                        v-validate="'required|min:2|max:100'"
                        outline
                    )

                v-flex(xs12 sm6)
                    v-select(
                        v-if="isAdmin"
                        :label="$t('statistics.select.business')"
                        :items="businesses"
                        v-model="businessId"
                        v-validate="'required'"
                        :data-vv-as="$t('statistics.select.business')"
                        :error-messages="errors.collect('businessId')"
                        name="businessId"
                        append-icon="expand_more"
                        outline
                    )

                    div(v-if="customFields && customFields.length")

                        // User custom fields block title
                        title-component(
                            type="h4"
                            v-if="isAdmin"
                            :title="$t('users.manualCreate.subtitleCustomFields')"
                        )

                        // Custom fields
                        v-text-field(
                            v-for="(item, key) in customFields"
                            :key="key"
                            :error-messages="errors.collect(`custom-field-${key}`)"
                            :data-vv-as="toFieldName(item.label)"
                            :label="toFieldName(item.label)"
                            :name="`custom-field-${key}`"
                            v-model="customFields[key].value"
                            v-validate="customValidation(customFields[key])"
                            outline
                        )

            v-layout(raw wrap)
                v-flex(xs12)

                    // Horizontal line
                    separator-component

                    div.controls
                        div

                            // Cancel button
                            button-component.ml-0(
                                :action="cancel"
                                :isLoading="isLoading"
                                :disabled="isLoading"
                                color="grey"
                                large
                                outline
                            ) {{ $t('defaultButtons.cancel') }}

                        div

                            // Submit button
                            button-component.mr-0.submit(
                                :action="submit"
                                :isLoading="isLoading"
                                :disabled="isLoading"
                                color="primary"
                                large
                            ) {{ $t('defaultButtons.save') }}

</template>

<script>
    import Button from '~components/Button';
    import Separator from '~components/Separator';
    import Title from '~components/Title';
    import { mapFields } from 'vuex-map-fields';
    import { mapActions, mapGetters } from 'vuex';
    import startCase from 'lodash/startCase';
    import document from '~api/document';

    export default {
        $_veeValidate: {
            validator: 'new'
        },
        components: {
            ButtonComponent: Button,
            SeparatorComponent: Separator,
            TitleComponent: Title
        },
        data() {
            return {
                businessId: null,
                maxUsers: 0,
                customFields: []
            };
        },
        computed: {
            ...mapFields('users', {
                model: 'user'
            }),
            ...mapGetters('users', ['isLoading']),
            ...mapGetters('auth', ['isAdmin', 'isChampion', 'user']),
            ...mapGetters('businesses', ['businesses'])
        },
        watch: {
            businessId(val) {
                const activeBusiness = this.businesses.filter(
                    item => item.value === val
                )[0];

                if (
                    activeBusiness &&
                    activeBusiness.customFields &&
                    activeBusiness.customFields.length
                ) {
                    this.customFields = activeBusiness.customFields;
                } else {
                    this.customFields = [];
                }

                if (activeBusiness && activeBusiness.maxUsers) {
                    this.maxUsers = activeBusiness.maxUsers;
                }
            }
        },
        async created() {
            if (this.isChampion) {
                this.businessId = this.user.id;
                this.maxUsers =
                    this.user.business && this.user.business.maxUsers
                        ? this.user.business.maxUsers
                        : 0;
                const resp = await document.readFields(this.businessId);
                this.customFields =
                    resp.data && resp.data.items ? resp.data.items : [];
            } else {
                this.getterBusinessesWithFields();
            }
        },
        mounted() {
            this.resetUser();
        },
        methods: {
            ...mapActions('users', ['create', 'resetUser', 'cancel']),
            ...mapActions('businesses', ['getterBusinessesWithFields']),

            /**
             * Validation for custom fields
             */
            customValidation(field) {
                let validation = '';

                if (field.isRequired) {
                    validation = 'required|';
                }
                if (field.type !== 'string') {
                    validation += 'numeric';
                }

                return validation;
            },

            /**
             * Create User account on submit
             * @returns {boolean}
             */
            async submit() {
                // Validate all fields and check in no errors
                const validation = await this.$validator.validateAll();
                if (!validation) return;

                this.customFields.map(elem => {
                    this.model[elem.label] =
                        elem.type === 'string' ? elem.value : +elem.value;
                });

                if (this.businessId) {
                    this.model.businessId = this.businessId;
                }

                // Call store action Create Business account
                this.create(this.businessId);
            },

            /**
             * Custom field name
             * @param {string} key
             * @returns {string}
             */
            fieldName(key) {
                return `fieldTitle-${ key }`;
            },

            /**
             * Transform camel case to field name
             * @param {string} label
             */
            toFieldName(label) {
                return startCase(label);
            }
        }
    };
</script>
